import {
  createMissingEntityTargetTitle,
  createMissingLoreTargetTitle,
  entityCardTypeLabels,
  entityItemCategoryLabels,
  entityRelationshipBuiltInLabels,
  entityRelationshipLabelLabels,
  fromStoredEntityCardType,
  fromStoredRelationshipKind,
  getEntityCardHref,
  getEntityLoreHref,
  type EntityCardLinkView,
  type EntityCardRecord,
  type EntityCardTargetOption,
  type EntityCardView,
  type EntityLimitPrompt,
  type EntityLoreCanonStatus,
  type EntityLoreOption,
  type EntityLoreType,
  type EntityRelationshipLabel,
  type EntityRelationshipRecord,
  type EntityRelationshipTargetOption,
  type EntityRelationshipView,
  type EntityWorkspaceSnapshot,
  type ProjectEntityCapacitySummary,
} from "@/types/entity";
import type { SubscriptionTier } from "@/types/projects";
import { parseEntityCardData } from "@/features/entities/validation/entitySchemas";
import { loreCanonStatusLabels } from "@/types/lore";

export type EntityLookupLoreEntry = {
  canon_status: EntityLoreCanonStatus;
  id: string;
  title: string;
  type: EntityLoreType;
};

function compactSummary(parts: Array<string | null | undefined>) {
  return parts
    .map((part) => part?.trim() ?? "")
    .filter(Boolean)
    .slice(0, 2)
    .join(" | ");
}

function getRelationshipLabelDetails(label: string): {
  customLabel: string | null;
  label: EntityRelationshipLabel;
  labelText: string;
} {
  if ((entityRelationshipBuiltInLabels as string[]).includes(label)) {
    const builtIn = label as Exclude<EntityRelationshipLabel, "custom">;
    return {
      customLabel: null,
      label: builtIn,
      labelText: entityRelationshipLabelLabels[builtIn],
    };
  }

  return {
    customLabel: label,
    label: "custom",
    labelText: label,
  };
}

function buildEntityLinkView(
  input:
    | {
        href: string | null;
        statusLabel: string | null;
        targetId: string | null;
        targetTitle: string;
      }
    | null,
) {
  if (!input?.targetId) {
    return null;
  }

  return {
    broken: !input.href,
    href: input.href,
    targetId: input.targetId,
    targetStatusLabel: input.statusLabel,
    targetTitle: input.targetTitle,
  } satisfies EntityCardLinkView;
}

function formatLoreTypeLabel(type: EntityLoreType) {
  return type.replaceAll("_", " ");
}

function buildCharacterSecondaryText(card: EntityCardView) {
  if (card.aliases.length > 0) {
    return `Aliases: ${card.aliases.join(", ")}`;
  }

  if (card.raceLink) {
    return `Race / Species: ${card.raceLink.targetTitle}`;
  }

  if (card.ageText.trim().length > 0) {
    return `Age: ${card.ageText}`;
  }

  return "Character card";
}

function buildItemSecondaryText(card: EntityCardView) {
  const details = [
    card.itemCategory ? entityItemCategoryLabels[card.itemCategory] : null,
    card.currentHolderLink ? `Held by ${card.currentHolderLink.targetTitle}` : null,
  ].filter(Boolean);

  return details.join(" | ") || "Item / Object card";
}

function buildCreatureSecondaryText(card: EntityCardView) {
  if (card.speciesLink) {
    return `Species: ${card.speciesLink.targetTitle}`;
  }

  if (card.creatureDescription.trim().length > 0) {
    return card.creatureDescription;
  }

  return "Creature card";
}

function buildLocationSecondaryText(card: EntityCardView) {
  if (card.locationDescription.trim().length > 0) {
    return card.locationDescription;
  }

  if (card.locationNotes.trim().length > 0) {
    return card.locationNotes;
  }

  return "Location card";
}

function buildMachineSecondaryText(card: EntityCardView) {
  const details = [
    card.machineCapabilities.trim().length > 0 ? card.machineCapabilities : null,
    card.currentHolderLink ? `Operated by ${card.currentHolderLink.targetTitle}` : null,
  ].filter(Boolean);

  if (details.length > 0) {
    return details.join(" | ");
  }

  if (card.machineDescription.trim().length > 0) {
    return card.machineDescription;
  }

  return "Machine card";
}

export function mapEntityCapacityRow(input: {
  entity_cards_limit: number | null;
  entity_cards_remaining: number | null;
  entity_cards_used: number;
  is_unlimited: boolean;
  tier: SubscriptionTier;
}): ProjectEntityCapacitySummary {
  return {
    entityCardsLimit: input.entity_cards_limit,
    entityCardsRemaining: input.entity_cards_remaining,
    entityCardsUsed: input.entity_cards_used,
    isUnlimited: input.is_unlimited,
    tier: input.tier,
  };
}

export function buildEntityLimitPrompt(
  tier: SubscriptionTier,
  currentCount: number,
  allowedCount: number,
  recommendedTier: SubscriptionTier,
): EntityLimitPrompt {
  return {
    allowedCount,
    currentCount,
    reason: "entity_card_limit_reached",
    recommendedTier,
    tier,
  };
}

export function parseEntityLimitError(message: string) {
  const [prefix, tier, currentCount, allowedCount, recommendedTier] = message.split("|");

  if (
    prefix !== "ENTITY_CARD_LIMIT_REACHED"
    || !tier
    || !currentCount
    || !allowedCount
    || !recommendedTier
  ) {
    return null;
  }

  return buildEntityLimitPrompt(
    tier as SubscriptionTier,
    Number(currentCount),
    Number(allowedCount),
    recommendedTier as SubscriptionTier,
  );
}

export function sortEntityCardsByUpdatedAt(cards: EntityCardView[]) {
  return [...cards].sort(
    (left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
  );
}

export function upsertEntityCard(cards: EntityCardView[], card: EntityCardView) {
  const nextCards = cards.filter((candidate) => candidate.id !== card.id);
  nextCards.push(card);
  return sortEntityCardsByUpdatedAt(nextCards);
}

export function removeEntityCard(cards: EntityCardView[], cardId: string) {
  return cards.filter((card) => card.id !== cardId);
}

export function mapEntityRelationshipRecordToView(
  record: EntityRelationshipRecord,
  projectId: string,
  entitiesById: Map<string, EntityCardView>,
  loreEntriesById: Map<string, EntityLookupLoreEntry>,
): EntityRelationshipView {
  const targetKind = fromStoredRelationshipKind(record.to_kind);
  const targetEntity = targetKind === "entity-card" ? entitiesById.get(record.to_id) ?? null : null;
  const targetLoreEntry = targetKind === "lore-entry" ? loreEntriesById.get(record.to_id) ?? null : null;
  const broken = !targetEntity && !targetLoreEntry;
  const label = getRelationshipLabelDetails(record.label);

  return {
    broken,
    createdAt: record.created_at,
    customLabel: label.customLabel,
    href: targetEntity
      ? getEntityCardHref(projectId, targetEntity.id)
      : targetLoreEntry
        ? getEntityLoreHref(projectId, targetLoreEntry.id)
        : null,
    id: record.id,
    label: label.label,
    labelText: label.labelText,
    targetId: record.to_id,
    targetKind,
    targetStatusLabel: targetEntity
      ? targetEntity.typeLabel
      : targetLoreEntry
        ? loreCanonStatusLabels[targetLoreEntry.canon_status]
        : "Missing",
    targetTitle: targetEntity?.name
      ?? targetLoreEntry?.title
      ?? (targetKind === "entity-card"
        ? createMissingEntityTargetTitle(record.to_id)
        : createMissingLoreTargetTitle(record.to_id)),
  };
}

export function mapEntityCardRecordToView(
  record: EntityCardRecord,
  input: {
    imageUrl: string | null;
    loreEntriesById: Map<string, EntityLookupLoreEntry>;
    projectRelationships: EntityRelationshipRecord[];
    projectRowsById: Map<string, EntityCardRecord>;
  },
): EntityCardView {
  const type = fromStoredEntityCardType(record.type);
  const relationshipsInvolvingCard = input.projectRelationships.filter(
    (relationship) =>
      (relationship.from_kind === "entity" && relationship.from_id === record.id)
      || (relationship.to_kind === "entity" && relationship.to_id === record.id),
  );
  const parsedData = parseEntityCardData(record.type, record.data);

  const raceLink = buildEntityLinkView(
    "raceLoreEntryId" in parsedData && parsedData.raceLoreEntryId
      ? {
          href: input.loreEntriesById.has(parsedData.raceLoreEntryId)
            ? getEntityLoreHref(record.project_id, parsedData.raceLoreEntryId)
            : null,
          statusLabel: input.loreEntriesById.get(parsedData.raceLoreEntryId)
            ? loreCanonStatusLabels[input.loreEntriesById.get(parsedData.raceLoreEntryId)!.canon_status]
            : "Missing",
          targetId: parsedData.raceLoreEntryId,
          targetTitle:
            input.loreEntriesById.get(parsedData.raceLoreEntryId)?.title
            ?? createMissingLoreTargetTitle(parsedData.raceLoreEntryId),
        }
      : null,
  );

  const speciesLink = buildEntityLinkView(
    "speciesLoreEntryId" in parsedData && parsedData.speciesLoreEntryId
      ? {
          href: input.loreEntriesById.has(parsedData.speciesLoreEntryId)
            ? getEntityLoreHref(record.project_id, parsedData.speciesLoreEntryId)
            : null,
          statusLabel: input.loreEntriesById.get(parsedData.speciesLoreEntryId)
            ? loreCanonStatusLabels[input.loreEntriesById.get(parsedData.speciesLoreEntryId)!.canon_status]
            : "Missing",
          targetId: parsedData.speciesLoreEntryId,
          targetTitle:
            input.loreEntriesById.get(parsedData.speciesLoreEntryId)?.title
            ?? createMissingLoreTargetTitle(parsedData.speciesLoreEntryId),
        }
      : null,
  );

  const currentHolderLink = buildEntityLinkView(
    "currentHolderEntityCardId" in parsedData && parsedData.currentHolderEntityCardId
      ? {
          href: input.projectRowsById.has(parsedData.currentHolderEntityCardId)
            ? getEntityCardHref(record.project_id, parsedData.currentHolderEntityCardId)
            : null,
          statusLabel: input.projectRowsById.get(parsedData.currentHolderEntityCardId)
            ? entityCardTypeLabels[
                fromStoredEntityCardType(
                  input.projectRowsById.get(parsedData.currentHolderEntityCardId)!.type,
                )
              ]
            : "Missing",
          targetId: parsedData.currentHolderEntityCardId,
          targetTitle:
            input.projectRowsById.get(parsedData.currentHolderEntityCardId)?.name
            ?? createMissingEntityTargetTitle(parsedData.currentHolderEntityCardId),
        }
      : null,
  );

  const baseCard: EntityCardView = {
    ageText: "ageText" in parsedData ? parsedData.ageText ?? "" : "",
    aliases: "aliases" in parsedData ? parsedData.aliases : [],
    createdAt: record.created_at,
    creatureAbilities: "abilities" in parsedData ? parsedData.abilities ?? "" : "",
    creatureDescription:
      "description" in parsedData && type === "creature" ? parsedData.description ?? "" : "",
    currentHolderLink,
    generateActionLabel: "entities.media.generateAction",
    hasBrokenLinks:
      Boolean(raceLink?.broken)
      || Boolean(speciesLink?.broken)
      || Boolean(currentHolderLink?.broken),
    hasImage: Boolean(record.image_path),
    id: record.id,
    imagePath: record.image_path,
    imageUrl: input.imageUrl,
    itemCategory: "itemCategory" in parsedData ? parsedData.itemCategory : null,
    itemDescription:
      "description" in parsedData && type === "item-object" ? parsedData.description ?? "" : "",
    locationDescription:
      "description" in parsedData && type === "location" ? parsedData.description ?? "" : "",
    locationNotes: "notes" in parsedData && type === "location" ? parsedData.notes ?? "" : "",
    machineCapabilities:
      "capabilities" in parsedData && type === "machine" ? parsedData.capabilities ?? "" : "",
    machineDescription:
      "description" in parsedData && type === "machine" ? parsedData.description ?? "" : "",
    name: record.name,
    notes: "notes" in parsedData ? parsedData.notes ?? "" : "",
    origin: "origin" in parsedData ? parsedData.origin ?? "" : "",
    personalityTraits: "personalityTraits" in parsedData ? parsedData.personalityTraits ?? "" : "",
    physicalDescription: "physicalDescription" in parsedData ? parsedData.physicalDescription ?? "" : "",
    projectId: record.project_id,
    raceLink,
    relationshipCount: relationshipsInvolvingCard.length,
    relationships: [],
    secondaryText: "",
    skillsAndAbilities: "skillsAndAbilities" in parsedData ? parsedData.skillsAndAbilities ?? "" : "",
    speciesLink,
    type,
    typeLabel: entityCardTypeLabels[type],
    updatedAt: record.updated_at,
  };

  baseCard.secondaryText =
    type === "character"
      ? buildCharacterSecondaryText(baseCard)
      : type === "item-object"
        ? buildItemSecondaryText(baseCard)
        : type === "creature"
          ? buildCreatureSecondaryText(baseCard)
          : type === "location"
            ? buildLocationSecondaryText(baseCard)
            : buildMachineSecondaryText(baseCard);

  return baseCard;
}

export function buildEntityRaceSpeciesOptions(loreEntries: EntityLookupLoreEntry[]): EntityLoreOption[] {
  return [...loreEntries]
    .filter((entry) => entry.type === "race_species")
    .sort((left, right) => left.title.localeCompare(right.title))
    .map((entry) => ({
      canonStatus: entry.canon_status,
      canonStatusLabel: loreCanonStatusLabels[entry.canon_status],
      id: entry.id,
      title: entry.title,
      type: entry.type,
    }));
}

export function buildEntityCharacterTargetOptions(rows: EntityCardRecord[]): EntityCardTargetOption[] {
  return rows
    .filter((row) => row.type === "character")
    .map((row) => ({
      description: entityCardTypeLabels.character,
      id: row.id,
      name: row.name,
      type: "character" as const,
    }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

export function buildEntityRelationshipTargetOptions(
  projectId: string,
  cards: EntityCardView[],
  loreEntries: EntityLookupLoreEntry[],
): EntityRelationshipTargetOption[] {
  return [
    ...cards.map((card) => ({
      description: card.typeLabel,
      href: getEntityCardHref(projectId, card.id),
      id: card.id,
      kind: "entity-card" as const,
      statusLabel: card.typeLabel,
      title: card.name,
    })),
    ...loreEntries.map((entry) => ({
      description: formatLoreTypeLabel(entry.type),
      href: getEntityLoreHref(projectId, entry.id),
      id: entry.id,
      kind: "lore-entry" as const,
      statusLabel: loreCanonStatusLabels[entry.canon_status],
      title: entry.title,
    })),
  ].sort((left, right) => left.title.localeCompare(right.title));
}

export function buildEntityWorkspaceSnapshot(input: {
  capacity: ProjectEntityCapacitySummary;
  imageUrlsByCardId: Map<string, string | null>;
  loreEntries: EntityLookupLoreEntry[];
  projectId: string;
  relationships: EntityRelationshipRecord[];
  rows: EntityCardRecord[];
}): EntityWorkspaceSnapshot {
  const loreEntriesById = new Map(input.loreEntries.map((entry) => [entry.id, entry]));
  const rowsById = new Map(input.rows.map((row) => [row.id, row]));
  const preliminaryCards = sortEntityCardsByUpdatedAt(
    input.rows.map((row) =>
      mapEntityCardRecordToView(row, {
        imageUrl: input.imageUrlsByCardId.get(row.id) ?? null,
        loreEntriesById,
        projectRelationships: input.relationships,
        projectRowsById: rowsById,
      }),
    ),
  );
  const cardsById = new Map(preliminaryCards.map((card) => [card.id, card]));
  const cards = preliminaryCards.map((card) => {
    const outgoingRelationships = input.relationships.filter(
      (relationship) => relationship.from_kind === "entity" && relationship.from_id === card.id,
    );
    const relationships = outgoingRelationships.map((relationship) =>
      mapEntityRelationshipRecordToView(
        relationship,
        card.projectId,
        cardsById,
        loreEntriesById,
      ),
    );

    return {
      ...card,
      hasBrokenLinks:
        card.hasBrokenLinks || relationships.some((relationship) => relationship.broken),
      relationships,
    };
  });

  return {
    capacity: input.capacity,
    cards,
    characterTargetOptions: buildEntityCharacterTargetOptions(input.rows),
    raceSpeciesOptions: buildEntityRaceSpeciesOptions(input.loreEntries),
    relationshipTargetOptions: buildEntityRelationshipTargetOptions(
      input.projectId,
      cards,
      input.loreEntries,
    ),
  };
}

export function summarizeEntityCard(card: EntityCardView) {
  return compactSummary([
    card.secondaryText,
    card.notes,
    card.personalityTraits,
    card.itemDescription,
    card.creatureDescription,
    card.locationDescription,
    card.locationNotes,
    card.machineDescription,
    card.machineCapabilities,
  ]);
}
